import type { Settings, BindableAction } from '../settings';
import { keyLabel } from '../settings';
import { IS_DEV } from '../config';
import type { Input } from '../engine/input';
import { SK, kbd, labelCss, titleCss, panelCss, injectSkinCss } from './skin';

// Single source of truth for the controls/help overlay (H key + menu button). Each row is either a
// rebindable action (looked up live from Settings.binds, so it always shows the PLAYER'S current keys,
// not the shipped defaults) or a fixed key that is not rebindable (mouse buttons, dev/admin toggles,
// F-keys). Grouped for the overlay; keep new keys added elsewhere in main.ts mirrored here so the sheet
// stays the one place that lists every control (owner ask: "generated from one table").
type Row = { label: string; keys: string } | { label: string; action: BindableAction };
interface Group { title: string; rows: Row[]; devOnly?: boolean }

const GROUPS: Group[] = [
  {
    title: 'Move',
    rows: [
      { label: 'Forward', action: 'forward' },
      { label: 'Back', action: 'back' },
      { label: 'Left', action: 'left' },
      { label: 'Right', action: 'right' },
      { label: 'Sprint', action: 'sprint' },
      { label: 'Crouch', action: 'crouch' },
      { label: 'Jump', action: 'jump' },
      { label: 'Peek left / right', action: 'leanLeft' },
    ],
  },
  {
    title: 'Camera',
    rows: [
      { label: 'Orbit view', keys: 'M-Mouse drag' },
      { label: 'Free-look (travel locked)', keys: 'Alt + mouse' },
      { label: 'Zoom / enter first person', keys: 'Scroll wheel' },
      { label: 'Toggle first person', keys: 'V' },
      { label: 'Map (open / peek)', keys: 'M / hold G' },
    ],
  },
  {
    title: 'Interact',
    rows: [
      { label: 'Interact / pick up / loot', action: 'interact' },
      { label: 'Inventory / character', action: 'inventory' },
      { label: 'Crafting menu', action: 'crafting' },
      { label: 'Select hotbar slot', keys: '1-0' },
    ],
  },
  {
    title: 'Build',
    rows: [
      { label: 'Build mode (tool wheel)', action: 'build' },
      { label: 'Rotate piece / cycle socket', action: 'buildHeight' },
      { label: 'Alt action (flip door / raise)', action: 'buildAlt' },
      { label: 'Demolish aimed piece (hammer out)', keys: 'X' },
    ],
  },
  {
    title: 'Combat',
    rows: [
      { label: 'Attack / gather / fire', keys: 'L-Mouse' },
      { label: 'Aim down sights (hold)', keys: 'R-Mouse (hold)' },
    ],
  },
  {
    title: 'Dev',
    devOnly: true,
    rows: [
      { label: 'Debug overlay (dev mode)', action: 'debug' },
      { label: 'Admin tuning panel', keys: 'F2' },
      { label: 'Biome tuning tool', keys: 'F3' },
      { label: 'Game-master admin page', keys: 'F4' },
      { label: 'Frame-cost diagnostics', keys: 'P' },
      { label: 'Toggle occlusion cull', keys: 'O' },
    ],
  },
];

// Resolve one row to its display label + current key chip text (rebindable actions read live).
function resolveRow(settings: Settings, row: Row): { label: string; keys: string } {
  if ('keys' in row) return { label: row.label, keys: row.keys };
  const codes = settings.binds[row.action];
  return { label: row.label, keys: codes.length ? codes.map(keyLabel).join(' / ') : '-' };
}

// Build the full grouped table (label + resolved key text), filtering dev-only groups out in production.
export function buildHelpTable(settings: Settings): Array<{ title: string; rows: Array<{ label: string; keys: string }> }> {
  return GROUPS.filter((g) => !g.devOnly || IS_DEV).map((g) => ({
    title: g.title,
    rows: g.rows.map((r) => resolveRow(settings, r)),
  }));
}

// Controls/help overlay: H key or a menu button opens it. Read-only (no rebinding here -- that stays in
// the Esc menu); this is the "what does everything do right now" cheat sheet. Same dim-backdrop-panel
// idiom as Menu (ui/menu.ts).
export class Help {
  open = false;
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;

  constructor(private readonly settings: Settings, private readonly input: Input) {
    injectSkinCss();
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:21', 'display:none',
      'align-items:center', 'justify-content:center',
      `background:${SK.scrim}`,
      `font-family:${SK.font}`,
    ].join(';');

    this.panel = document.createElement('div');
    this.panel.style.cssText = [
      'width:min(560px,92vw)', 'max-height:86vh', 'overflow-y:auto',
      panelCss('menu'), 'padding:24px', 'box-shadow:0 18px 50px rgba(0,0,0,0.6)',
    ].join(';');
    this.root.appendChild(this.panel);
    document.body.appendChild(this.root);

    this.root.addEventListener('pointerdown', (e) => { if (e.target === this.root) this.close(); });
    this.render();
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  show(): void {
    this.open = true;
    this.root.style.display = 'flex';
    this.input.setSuppressed(true); // pause gameplay input while the sheet is up, same as the pause menu
    this.render();
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.root.style.display = 'none';
    this.input.setSuppressed(false);
  }

  private render(): void {
    const p = this.panel;
    p.replaceChildren();
    const head = document.createElement('div');
    head.style.cssText = 'display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px;';
    head.appendChild(text('div', 'CONTROLS', titleCss()));
    const closeHint = text('div', '', `color:${SK.dim};font-size:12px;`);
    closeHint.innerHTML = `${kbd('H')} or ${kbd('Esc')} to close`;
    head.appendChild(closeHint);
    p.appendChild(head);
    p.appendChild(text('div', 'Movement/build/interact keys are rebindable in the pause menu.', `color:${SK.dim};font-size:12px;margin:2px 0 12px;`));

    for (const group of buildHelpTable(this.settings)) {
      p.appendChild(text(
        'div', group.title,
        labelCss() + `;margin:16px 0 8px;border-bottom:1px solid ${SK.border};padding-bottom:4px;`,
      ));
      for (const row of group.rows) {
        const r = document.createElement('div');
        r.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:12px;padding:4px 0;';
        r.appendChild(text('span', row.label, `color:${SK.text};font-size:13px;`));
        // Each key renders as a chip (keys-are-chips); '-' = unbound, shown faint.
        const keys = document.createElement('span');
        keys.style.cssText = 'display:inline-flex;gap:4px;flex-wrap:wrap;justify-content:flex-end;align-items:center;';
        if (row.keys === '-') { keys.textContent = '-'; keys.style.color = SK.faint; }
        else keys.innerHTML = row.keys.split(' / ').map((k) => kbd(k)).join('');
        r.appendChild(keys);
        p.appendChild(r);
      }
    }
  }
}

function text(tag: string, content: string, css: string): HTMLElement {
  const el = document.createElement(tag);
  el.textContent = content;
  el.style.cssText = css;
  return el;
}
